import { subject } from '@casl/ability'
import { isV2Case } from '@features/logs/components/incidents/v2/usesV1Form'
import { incidentAttachments } from '@features/logs/components/incidents/v2/view/readValues'
import useLog from '@features/logs/hooks/useLog'
import { colors } from '@lmc/common/constants'
import { DocumentIcon, HomeIcon } from '@lmc/design-system'
import { type Home, IncidentStatus, UserRole } from '@lmc/ts-common'
import {
  AvatarLabel,
  Edit,
  EmptyState,
  NoDataIcon,
  PageHeader,
  Restore,
  Tag,
  Trash
} from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import useGoalsAccess from 'components/AccessControl/features/useGoalsAccess'
import { Spinner } from 'components/generic'
import Description from 'components/generic/LogTimelineRow/Description'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import usePdfExport from 'hooks/usePdfExport'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { logsConstants } from 'locale/logs'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { getPdfTitle, tagColorMap } from '../../utils/incidentUtils'
import {
  Card,
  DescriptionSection,
  ResidentSection,
  TitledBlock
} from '../log/LogDetailsV2'
import LogLinkedGoals from '../log/LogLinkedGoals'
import LogViewFooter, {
  FooterRow,
  PersonAvatar,
  SectionTitle
} from '../log/LogViewFooter'
import AttachmentsRow from './AttachmentsRow'
import {
  HeaderChildrenContainer,
  IconContainer,
  TitleContainer,
  Wrapper
} from './commonStyledComponents'
import {
  IncidentCasePanel,
  IncidentPdfButton,
  IncidentScreenContainer
} from './IncidentCaseVersion'
import CloseIncidentModal from './modals/CloseIncidentModal'
import RemoveIncidentModal from './modals/RemoveIncidentModal'
import ReopenIncidentModal from './modals/ReopenIncidentModal'
import RestoreIncidentModal from './modals/RestoreIncidentModal'
import ReviewLogDropdownButton from './ReviewLogDropdownButton'

type IncidentReportScreenV2Props = {
  testId?: string
}

const IncidentReportScreenV2 = ({
  testId = 'incident-detail'
}: IncidentReportScreenV2Props = {}) => {
  const navigate = useNavigate()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { t: tLogs } = useTranslation(NAMESPACE.LOGS)
  const hasGoalsAccess = useGoalsAccess()
  const { id: incidentLogId } = useParams()
  const { incidentReport, isIncidentReportLoading, refetch } =
    useIncidentReportByLog({
      incidentLogId
    })
  const [isClosingIncident, setIsClosingIncident] = useState(false)
  const [isReopeningIncident, setIsReopeningIncident] = useState(false)
  const [isRestoringIncident, setIsRestoringIncident] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [hasDownloadStarted, setHasDownloadStarted] = useState(false)
  const { removeLog, isRemoveLoading } = useLog({
    logId: incidentLogId,
    onRemoveSuccess: () => {
      setIsRemoveModalOpen(false)
      showToast({
        title: t(incidentReportsConstants.LOG_REMOVED)
      })
      refetch()
    }
  })
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const pdfContent = useRef<HTMLInputElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const handleDownloadPdf = usePdfExport(
    pdfContent,
    incidentReport
      ? getPdfTitle(incidentReport, t(incidentReportsConstants.INCIDENT_CASE))
      : t(incidentReportsConstants.INCIDENT_CASE),
    setIsDownloading
  )

  const gpsLocationEnabled = home.features?.geoLocation?.enabled
  const hasLocationsEnabled = home?.features?.hasHomeLocations?.enabled

  const location = useLocation()
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const rootPage = pathSegments.slice(0, -1).join('/')

  const logsPath = `/${rootPage}/${incidentLogId}`

  const onCreateCaseClick = () => {
    navigate(`${logsPath}/create-case`)
  }

  const onCloseIncidentClick = () => {
    setIsClosingIncident(true)
  }

  const onDownloadPDFClick = () => {
    setHasDownloadStarted(true)
  }

  const handleRemoveLog = async () => {
    try {
      await removeLog()
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (hasDownloadStarted) {
      handleDownloadPdf()
      setHasDownloadStarted(false)
    }
  }, [hasDownloadStarted])

  const hasPermissionToCreateCase = useAbilityAccess([
    {
      i: 'update',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  const hasPermissionToEditCase = useAbilityAccess([
    {
      i: 'update',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  // CM-4059: closing an incident is a Delete-tier action on the backend
  // (closeIncidentReport requires Action.Delete on Incident), so gate on `delete`
  // — Senior Carers have update-but-not-delete and must not see an enabled Close.
  const hasPermissionToCloseCase = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  const hasPermissionToRemoveLog = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  if (isIncidentReportLoading)
    return <Spinner data-testid={`${testId}-spinner`} />
  if (!incidentReport || Object.keys(incidentReport).length <= 0)
    return (
      <EmptyState
        data-testid={`${testId}-empty-state`}
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  // hasCase is true if the incident report has a case opened and if isDraft is falsy
  // that means that the case was opened by a user and not pre-populated
  const hasCase = incidentReport.case && !incidentReport.case.isDraft
  const isV2 = isV2Case(incidentReport)

  const isClosed = incidentReport.status === IncidentStatus.closed

  const isDeleted = incidentReport.log.deleted

  const showLocation = incidentReport.log?.location && hasLocationsEnabled

  const serviceUserPicture = incidentReport.serviceUser
    ?.picture as unknown as string

  const attachments = incidentAttachments(incidentReport)

  return (
    <IncidentScreenContainer isV2={isV2}>
      <PageHeader
        testId={`${testId}`}
        sticky
        title={
          <TitleContainer data-testid={`${testId}-title-container`}>
            <IconContainer
              data-testid={`${testId}-icon`}
              src={
                (incidentReport.log.item?.icon ||
                  incidentReport.log.category?.icon) as unknown as string
              }
              $color={incidentReport.log.category.color}
              alt="icon"
            />
            {incidentReport.log.title}
            {isDeleted ? (
              <Tag data-testid={`${testId}-removed-tag`}>
                {t(incidentReportsConstants.REMOVED)}
              </Tag>
            ) : (
              <Tag
                data-testid={`${testId}-status-tag`}
                color={tagColorMap[incidentReport.status]}
              >
                {t(incidentReportsConstants[incidentReport.status])}
              </Tag>
            )}
          </TitleContainer>
        }
        onBack={() => navigate('../', { relative: 'path' })}
      >
        <HeaderChildrenContainer data-testid={`${testId}-header-actions`}>
          {hasCase && !isClosed && (
            <IncidentPdfButton
              incidentReport={incidentReport}
              isV2={isV2}
              isDownloading={isDownloading}
              onDownload={onDownloadPDFClick}
              testId={`${testId}-download-pdf-button`}
            />
          )}
          <Link
            data-testid={`${testId}-edit-link`}
            to={hasCase ? `${logsPath}/edit-case` : `${logsPath}/edit`}
          >
            <Button
              data-testid={`${testId}-edit-button`}
              disabled={incidentReport.log.deleted || isClosed}
              variant={hasCase ? 'primary' : 'default'}
              disabledByPermission={!hasPermissionToEditCase}
            >
              <Edit />
              {t(incidentReportsConstants.EDIT)}
            </Button>
          </Link>
          {isClosed && (
            <Button
              data-testid={`${testId}-reopen-button`}
              onClick={() => {
                setIsReopeningIncident(true)
              }}
            >
              <Restore /> {t(incidentReportsConstants.REOPEN_INCIDENT)}
            </Button>
          )}
          {hasCase && isClosed && (
            <IncidentPdfButton
              incidentReport={incidentReport}
              isV2={isV2}
              isDownloading={isDownloading}
              onDownload={onDownloadPDFClick}
              testId={`${testId}-download-pdf-button-closed`}
            />
          )}
          {/* CM-4663: Remove lives here rather than on an edit screen because
              Edit is disabled once an incident is closed — so the edit screens
              are unreachable in exactly the state where removal is allowed.
              Removal is deliberately blocked while a case is still open. */}
          {isClosed && !isDeleted && (
            <Button
              data-testid={`${testId}-remove-button`}
              variant="danger"
              onClick={() => setIsRemoveModalOpen(true)}
              disabledByPermission={!hasPermissionToRemoveLog}
            >
              <Trash />
              {t(incidentReportsConstants.REMOVE)}
            </Button>
          )}
          {isDeleted && (
            <Button
              data-testid={`${testId}-restore-button`}
              onClick={() => {
                setIsRestoringIncident(true)
              }}
            >
              <Restore /> {t(incidentReportsConstants.RESTORE)}
            </Button>
          )}
          {!isClosed && !hasCase && !isDeleted && (
            <ReviewLogDropdownButton
              data-testid={`${testId}-review-dropdown`}
              hasPermissionToCloseCase={hasPermissionToCloseCase}
              hasPermissionToCreateCase={hasPermissionToCreateCase}
              onCloseIncidentClick={onCloseIncidentClick}
              onCreateCaseClick={onCreateCaseClick}
            />
          )}
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper data-testid={`${testId}-wrapper`} ref={pdfContent}>
        {!isV2 && (
          <Card data-testid={`${testId}-log-card`}>
            <ResidentSection data-testid={`${testId}-name-section`}>
              <AvatarLabel
                onSelect={() =>
                  navigate(
                    `/residents/${incidentReport.serviceUser?.id}/profile`
                  )
                }
                label={incidentReport.serviceUser.name}
                picture={serviceUserPicture}
                testId={`${testId}-service-user`}
              />
            </ResidentSection>
            <DescriptionSection data-testid={`${testId}-description-section`}>
              <SectionTitle as="h2">
                {t(incidentReportsConstants.DESCRIPTION)}
              </SectionTitle>
              <DescriptionText>
                <Description
                  data-testid={`${testId}-description`}
                  description={incidentReport.log.description}
                  downloadStarted={hasDownloadStarted}
                />
              </DescriptionText>
              {incidentReport.log.witnessedBy?.name && (
                <DescriptionText
                  data-testid={`${testId}-description-witnessed-by`}
                >
                  {tLogs(logsConstants.LOG_VIEW_WITNESSED_BY_NAME, {
                    name: incidentReport.log.witnessedBy.name
                  })}
                </DescriptionText>
              )}
            </DescriptionSection>
            <LogViewFooter
              isNextHandover={incidentReport.log.isNextHandover}
              timeLogged={incidentReport.log.timeLogged}
              loggedBy={incidentReport.log.loggedBy}
              witnessedBy={incidentReport.log.witnessedBy}
              editedBy={incidentReport.log.editedBy}
              editedOn={incidentReport.log.editedAt}
              showLocation={Boolean(gpsLocationEnabled)}
              latitude={incidentReport.log.latitude}
              longitude={incidentReport.log.longitude}
              accuracy={incidentReport.log.accuracy}
              activityExtra={
                <>
                  {showLocation && (
                    <FooterRow
                      icon={<HomeIcon size={24} />}
                      label={t(incidentReportsConstants.LOCATION)}
                      value={incidentReport.log.location}
                      testId={`${testId}-location-section`}
                    />
                  )}
                  {incidentReport.closedBy && isClosed && (
                    <FooterRow
                      icon={<PersonAvatar person={incidentReport.closedBy} />}
                      label={t(incidentReportsConstants.CLOSED_BY)}
                      value={incidentReport.closedBy.name}
                      testId={`${testId}-closed-by-section`}
                    />
                  )}
                  {incidentReport.closedBy &&
                    isClosed &&
                    incidentReport.closeComment && (
                      <FooterRow
                        icon={<DocumentIcon size={24} />}
                        label={t(incidentReportsConstants.CLOSE_COMMENT)}
                        value={incidentReport.closeComment}
                        testId={`${testId}-close-comment-section`}
                      />
                    )}
                </>
              }
              media={
                attachments.length > 0 ? (
                  <TitledBlock data-testid={`${testId}-attachments-section`}>
                    <SectionTitle>
                      {t(incidentReportsConstants.ATTACHMENTS)}
                    </SectionTitle>
                    <AttachmentsRow
                      attachments={attachments}
                      testId={`${testId}-attachments`}
                    />
                  </TitledBlock>
                ) : undefined
              }
              goals={
                hasGoalsAccess && incidentReport.log.goals?.length ? (
                  <LogLinkedGoals
                    goals={incidentReport.log.goals}
                    residentId={incidentReport.serviceUser?.id}
                    testId={`${testId}-goals`}
                  />
                ) : undefined
              }
              testId={`${testId}-footer`}
            />
          </Card>
        )}
        <IncidentCasePanel
          incidentReport={incidentReport}
          isV2={isV2}
          hasCase={Boolean(hasCase)}
          severities={home.incidentSeverity}
          testId={`${testId}-case-view`}
        />
        <CloseIncidentModal
          data-testid={`${testId}-close-modal`}
          open={isClosingIncident}
          setIsClosingIncident={setIsClosingIncident}
          incidentReport={incidentReport}
          onSuccess={refetch}
        />
        <ReopenIncidentModal
          data-testid={`${testId}-reopen-modal`}
          open={isReopeningIncident}
          setIsReopeningIncident={setIsReopeningIncident}
          incidentReport={incidentReport}
          refetch={refetch}
        />
        <RemoveIncidentModal
          data-testid={`${testId}-remove-modal`}
          open={isRemoveModalOpen}
          onConfirm={handleRemoveLog}
          onCancel={() => setIsRemoveModalOpen(false)}
          isRemoveLoading={isRemoveLoading}
        />
        <RestoreIncidentModal
          data-testid={`${testId}-restore-modal`}
          open={isRestoringIncident}
          setIsRestoringIncident={setIsRestoringIncident}
          logId={incidentReport.log.id}
          refetch={refetch}
        />
      </Wrapper>
    </IncidentScreenContainer>
  )
}

const DescriptionText = styled.div`
  color: ${colors.black};
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  white-space: pre-wrap;
`

export default IncidentReportScreenV2
